import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { BlobProvider, Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import logo from 'images/hostplatform-logo.png';
import { getPricingModelByHostId } from 'utils/apis/host';
import moment from 'moment';
import { capitalizeFirstLetter } from 'utils/general';

Font.register('https://nebulapi-public-prd.s3.ap-southeast-1.amazonaws.com/chinese.msyh.ttf', { family: 'Microsoft YaHei' });

Font.register('https://nebula-stg-images.s3.ap-southeast-1.amazonaws.com/Roboto-Bold.ttf', { family: 'Roboto' });

Font.registerEmojiSource({
  format: 'png',
  url: 'https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/'
});

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#FFFFFF',
    padding: 30
  },
  pageInventorySummary: {
    backgroundColor: '#FFFFFF',
    padding: 15
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5
  },
  logo: {
    width: 130,
    height: 70,
    marginRight: 5
  },
  companyInfo: {
    fontSize: 8,
    flex: 1,
    marginRight: 20
  },
  invoice: {
    fontSize: 24,
    fontWeight: 'bolder'
  },
  line: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    marginVertical: 6
  },
  hostInfoContainer: {
    flexDirection: 'row',
    fontSize: 8,
    padding: 25,
    paddingBottom: 0,
    paddingTop: 0
  },
  hostInfoLeft: {
    flex: 1,
    alignItems: 'flex-start'
  },
  hostInfoRight: {
    flex: 1,
    alignItems: 'flex-end',
    marginRight: 60
  },
  invoiceInfoRight: {
    flex: 1,
    alignItems: 'flex-end',
    marginRight: 14
  },
  hostInfoText: {
    flexDirection: 'row',
    marginBottom: 5,
    alignItems: 'flex-start'
  },
  hostInfoLabel: {
    width: 40,
    marginRight: 5
  },
  hostInfoValue: {
    flex: 1
  },
  borderBottom: {
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderColor: 'black'
  },
  borderTop: {
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderColor: 'black'
  },
  borderRight: {
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    borderColor: 'black'
  },
  borderLeft: {
    borderLeftWidth: 1,
    borderLeftStyle: 'solid',
    borderColor: 'black'
  },
  paymentInfo: {
    marginTop: 30,
    fontSize: 8
  },
  paymentInfoText: {
    paddingBottom: 5
  },
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: 'auto'
  },
  tableInventorySummaryHeader: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%'
  },
  tableCell: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    paddingTop: 6
  },
  productName: {
    textAlign: 'left',
    paddingLeft: 8,
    fontWeight: 'bold',
    textDecoration: 'underline',
    paddingBottom: 2
  },
  productCode: {
    textAlign: 'left',
    paddingLeft: 8,
    fontWeight: 'bolder',
    paddingBottom: 2
  },
  productText: {
    textAlign: 'left',
    paddingLeft: 8
  },
  productDescription: {
    textAlign: 'left',
    paddingLeft: 8,
    paddingBottom: 12
  },
  footerLabel: {
    textAlign: 'right',
    paddingRight: 5,
    fontWeight: 'bold'
  },
  footerValue: {
    textAlign: 'right',
    paddingRight: 2
  }
});

// Define the PDF Document
const SubscriptionInvoicePDF = ({
  systemProducts,
  taxesApplied,
  subTotal,
  subTotalAfterDiscount,
  systemProductSubTotal,
  grandTotal,
  inventories,
  additionalProducts,
  hostProtectProducts,
  modifiedInventories,
  date,
  invoiceRef,
  remark,
  discountApplied,
  inventoryChunks,
  hostName,
  subscriptionDesc,
  subscriptionType,
  priceTier,
  hasParentChildHost
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Image style={styles.logo} src={logo} />
        <View style={styles.companyInfo}>
          <Text>HOST PLATFORM SDN BHD (1238452-V)</Text>
          <Text>LOT NO 7F-2, 7 FLOOR, TOWER 4, @ PFCC,</Text>
          <Text>JALAN PUTERI 1/2, BANDAR PUTERI,</Text>
          <Text>47100, PUCHONG,SELANGOR MALAYSIA</Text>
          <Text>Phone : 03-7886 7829</Text>
        </View>
        <Text style={styles.invoice}>P-INVOICE</Text>
      </View>

      <View style={styles.line} />

      {/*<! -------------- Table Header --------------------!> */}

      <View style={styles.hostInfoContainer}>
        <View style={styles.hostInfoLeft}>
          <View style={styles.hostInfoText}>
            <Text style={styles.hostInfoLabel}>Host</Text>
            <Text style={styles.hostInfoValue}>: {hostName}</Text>
          </View>
          <View style={styles.hostInfoText}>
            <Text style={styles.hostInfoLabel}>Account</Text>
            <Text style={styles.hostInfoValue}>: </Text>
          </View>
          <View style={styles.hostInfoText}>
            <Text style={styles.hostInfoLabel}>Remark</Text>
            <Text style={styles.hostInfoValue}>: {remark}</Text>
          </View>
        </View>
        <View style={styles.hostInfoRight}>
          <View style={styles.hostInfoText}>
            <Text style={styles.hostInfoLabel}>Date</Text>
            <Text style={styles.hostInfoValue}>: {date}</Text>
          </View>
          <View style={styles.hostInfoText}>
            <Text style={styles.hostInfoLabel}>REF</Text>
            <Text style={styles.hostInfoValue}>: {invoiceRef}</Text>
          </View>
        </View>
      </View>

      <View style={styles.hostInfoContainer}>
        <View style={styles.hostInfoLeft}>
          <View style={styles.hostInfoText}>
            <Text style={styles.hostInfoLabel}></Text>
            <Text style={styles.hostInfoValue}>{subscriptionDesc}</Text>
          </View>
        </View>
        <View style={styles.invoiceInfoRight}>
          <Text style={styles.hostInfoValue}>
            Pricing Tier {'  '} {priceTier} {'  '} {capitalizeFirstLetter(subscriptionType)}
          </Text>
        </View>
      </View>

      <View style={{ ...styles.tableHeader, backgroundColor: '#D3D3D3' }}>
        <View
          style={{
            ...styles.tableCell,
            width: '15%',
            ...styles.borderBottom,
            ...styles.borderTop,
            ...styles.borderLeft
          }}
        >
          <Text style={{ fontSize: 8 }}>Product Code</Text>
        </View>
        <View
          style={{
            ...styles.tableCell,
            width: '55%',
            ...styles.borderBottom,
            ...styles.borderTop,
            ...styles.borderLeft
          }}
        >
          <Text style={{ fontSize: 8 }}>Description</Text>
        </View>
        <View
          style={{
            ...styles.tableCell,
            width: '6%',
            ...styles.borderBottom,
            ...styles.borderTop,
            ...styles.borderLeft
          }}
        >
          <Text style={{ fontSize: 8 }}>Item</Text>
        </View>
        <View
          style={{
            ...styles.tableCell,
            width: '6%',
            ...styles.borderBottom,
            ...styles.borderTop,
            ...styles.borderLeft
          }}
        >
          <Text style={{ fontSize: 8 }}>Qty</Text>
        </View>
        <View
          style={{
            ...styles.tableCell,
            width: '18%',
            ...styles.borderBottom,
            ...styles.borderTop,
            ...styles.borderLeft,
            ...styles.borderRight
          }}
        >
          <Text style={{ fontSize: 8 }}>Price (RM)</Text>
          <View style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
            <View
              style={{
                width: '50%',
                ...styles.borderRight
              }}
            >
              <Text style={{ fontSize: 8, paddingTop: 5 }}>List Price</Text>
            </View>
            <View style={{ width: '50%' }}>
              <Text style={{ fontSize: 8, paddingTop: 5 }}>Total</Text>
            </View>
          </View>
        </View>
      </View>

      {/*<! -------------- Table Body (System) --------------------!> */}

      {systemProducts.map((product, index) => (
        <View key={index} style={styles.tableHeader}>
          <View style={{ ...styles.tableCell, width: '15%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8, ...styles.productCode, fontFamily: 'Roboto' }}>{product.code}</Text>
            <View>
              {product.subProduct && product.subProduct.subCode && product.subProduct.subDescription
                ? product.subProduct.subCode.map((subCode, subIndex) => (
                    <Text key={subIndex} style={{ fontSize: 8, ...styles.productText }}>
                      {subCode}
                    </Text>
                  ))
                : null}
            </View>
          </View>
          <View style={{ ...styles.tableCell, width: '55%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8, ...styles.productName, fontFamily: 'Roboto' }}>{product.name}</Text>
            <View>
              {product.subProduct && product.subProduct.subCode && product.subProduct.subDescription
                ? product.subProduct.subDescription.map((subDescription, subIndex) => (
                    <Text key={subIndex} style={{ fontSize: 8, ...styles.productText }}>
                      {subDescription}
                    </Text>
                  ))
                : null}
            </View>
            <View>
              <Text style={{ fontSize: 8, ...styles.productDescription }}>{product.description || ''}</Text>
            </View>
          </View>
          <View style={{ ...styles.tableCell, width: '6%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8 }}>{product.categoryParent[0] === 'system' ? 'unit' : ''}</Text>
          </View>
          <View style={{ ...styles.tableCell, width: '6%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8 }}>{product.inventoryCount}</Text>
          </View>
          <View
            style={{
              ...styles.tableCell,
              width: '18%',
              ...styles.borderLeft,
              ...styles.borderRight,
              display: 'flex',
              flexDirection: 'row',
              paddingTop: 0
            }}
          >
            <View style={{ width: '50%', ...styles.borderRight, borderRightWidth: '1', borderRighStyle: 'solid', borderColor: 'black' }}>
              <Text style={{ fontSize: 8, paddingTop: 7 }}>{product.pricing}</Text>
            </View>
            <View style={{ width: '50%' }}>
              <Text style={{ fontSize: 8, paddingTop: 7 }}>{product.totalPrice.toFixed(2)}</Text>
            </View>
          </View>
        </View>
      ))}

      {/*<! -------------- Table Body (Added Products) --------------------!> */}

      {additionalProducts.map((product, index) => (
        <View key={index} style={styles.tableHeader}>
          <View style={{ ...styles.tableCell, width: '15%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8 }}>{product.code}</Text>
          </View>
          <View style={{ ...styles.tableCell, width: '55%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8, ...styles.productName }}>{product.name}</Text>
            <View>
              <Text style={{ fontSize: 8, ...styles.productDescription }}>{product.description || ''}</Text>
            </View>
          </View>
          <View style={{ ...styles.tableCell, width: '6%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8 }}>
              {product.categoryParent[0] === 'hostProtect '
                ? 'unit'
                : product.categoryParent[0] === 'Integration' || product.categoryParent[0] === 'Misc'
                ? 'LS'
                : product.categoryParent[0] === 'Training'
                ? 'session'
                : ''}
            </Text>
          </View>
          <View style={{ ...styles.tableCell, width: '6%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8 }}>{product.quantity}</Text>
          </View>
          <View
            style={{
              ...styles.tableCell,
              width: '18%',
              ...styles.borderLeft,
              ...styles.borderRight,
              display: 'flex',
              flexDirection: 'row',
              paddingTop: 0
            }}
          >
            <View style={{ width: '50%', ...styles.borderRight, borderRightWidth: '1', borderRighStyle: 'solid', borderColor: 'black' }}>
              <Text style={{ fontSize: 8, paddingTop: 7 }}>{product.pricing}</Text>
            </View>
            <View style={{ width: '50%' }}>
              <Text style={{ fontSize: 8, paddingTop: 7 }}>{product.totalPrice.toFixed(2)}</Text>
            </View>
          </View>
        </View>
      ))}

      {/*<! -------------- Table Body (Modified Sub Fee Products) --------------------!> */}

      {modifiedInventories.map((product, index) => (
        <View key={index} style={styles.tableHeader} wrap={false}>
          <View style={{ ...styles.tableCell, width: '15%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8, ...styles.productCode, fontFamily: 'Roboto' }}>{product.code}</Text>
            <View>
              {product.subProduct && product.subProduct.subCode && product.subProduct.subDescription
                ? product.subProduct.subCode.map((subCode, subIndex) => (
                    <Text key={subIndex} style={{ fontSize: 8, ...styles.productText }}>
                      {subCode}
                    </Text>
                  ))
                : null}
            </View>
          </View>
          <View style={{ ...styles.tableCell, width: '55%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8, ...styles.productName, fontFamily: 'Roboto' }}>{product.name}</Text>
            <View>
              {product.subProduct && product.subProduct.subCode && product.subProduct.subDescription
                ? product.subProduct.subDescription.map((subDescription, subIndex) => (
                    <Text key={subIndex} style={{ fontSize: 8, ...styles.productText }}>
                      {subDescription}
                    </Text>
                  ))
                : null}
            </View>
            <View>
              <Text style={{ fontSize: 8, ...styles.productDescription }}>{product.description || ''}</Text>
            </View>
          </View>
          <View style={{ ...styles.tableCell, width: '6%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8 }}>{product.categoryParent[0] === 'system' ? 'unit' : ''}</Text>
          </View>
          <View style={{ ...styles.tableCell, width: '6%', ...styles.borderLeft }}>
            <Text style={{ fontSize: 8 }}>{product.inventoryCount}</Text>
          </View>
          <View
            style={{
              ...styles.tableCell,
              width: '18%',
              ...styles.borderLeft,
              ...styles.borderRight,
              display: 'flex',
              flexDirection: 'row',
              paddingTop: 0
            }}
          >
            <View style={{ width: '50%', ...styles.borderRight, borderRightWidth: '1', borderRighStyle: 'solid', borderColor: 'black' }}>
              <Text style={{ fontSize: 8, paddingTop: 7 }}>{product.subscriptionFee}</Text>
            </View>
            <View style={{ width: '50%' }}>
              <Text style={{ fontSize: 8, paddingTop: 7 }}>{product.totalPrice.toFixed(2)}</Text>
            </View>
          </View>
        </View>
      ))}

      {/*<! -------------- Table Footer --------------------!> */}

      <View style={{ ...styles.tableHeader }} wrap={false}>
        <View
          style={{
            ...styles.tableCell,
            width: '15%',
            ...styles.borderLeft,
            ...styles.borderTop
          }}
        ></View>
        <View
          style={{
            ...styles.tableCell,
            width: '55%',
            backgroundColor: '#D3D3D3',
            ...styles.borderLeft,
            ...styles.borderTop
          }}
        >
          <Text style={{ fontSize: 8, ...styles.footerLabel, fontFamily: 'Roboto' }}>Sub-Total (Before Tax)</Text>
        </View>
        <View
          style={{
            ...styles.tableCell,
            width: '6%',
            ...styles.borderLeft,
            ...styles.borderTop
          }}
        ></View>
        <View
          style={{
            ...styles.tableCell,
            width: '6%',
            ...styles.borderLeft,
            ...styles.borderTop
          }}
        ></View>
        <View
          style={{
            ...styles.tableCell,
            width: '18%',
            ...styles.borderLeft,
            ...styles.borderRight,
            ...styles.borderTop,

            display: 'flex',
            flexDirection: 'row',
            paddingTop: 0
          }}
        >
          <View
            style={{
              width: '50%',
              height: 'auto',
              borderRightWidth: 1,
              borderRightStyle: 'solid',
              borderColor: 'black'
            }}
          ></View>
          <View style={{ width: '50%' }}>
            <Text style={{ fontSize: 8, paddingTop: 7, ...styles.footerValue }}>{subTotal.toFixed(2)}</Text>
          </View>
        </View>
      </View>

      {/*<! -------------- Table Footer - Discount--------------------!> */}

      {discountApplied._id && (
        <View wrap={false}>
          <View style={{ ...styles.tableHeader }}>
            <View
              style={{
                ...styles.tableCell,
                width: '15%',
                ...styles.borderLeft,
                ...styles.borderTop
              }}
            ></View>
            <View
              style={{
                ...styles.tableCell,
                width: '55%',
                backgroundColor: '#D3D3D3',
                ...styles.borderLeft,
                ...styles.borderTop
              }}
            >
              <Text style={{ fontSize: 8, ...styles.footerLabel, fontFamily: 'Roboto' }}>
                {discountApplied.name} @ {discountApplied.amount}
                {discountApplied.type === 'percentage' ? '%' : ''}
              </Text>
            </View>
            <View
              style={{
                ...styles.tableCell,
                width: '6%',
                ...styles.borderLeft,
                ...styles.borderTop
              }}
            ></View>
            <View
              style={{
                ...styles.tableCell,
                width: '6%',
                ...styles.borderLeft,
                ...styles.borderTop
              }}
            ></View>
            <View
              style={{
                ...styles.tableCell,
                width: '18%',
                ...styles.borderLeft,
                ...styles.borderRight,
                ...styles.borderTop,
                display: 'flex',
                flexDirection: 'row',
                paddingTop: 0
              }}
            >
              <View
                style={{
                  width: '50%',
                  height: 'auto',
                  borderRightWidth: 1,
                  borderRightStyle: 'solid',
                  borderColor: 'black'
                }}
              ></View>
              <View style={{ width: '50%' }}>
                <Text style={{ fontSize: 8, paddingTop: 7, ...styles.footerValue }}>
                  -
                  {discountApplied.type === 'percentage'
                    ? (systemProductSubTotal * (discountApplied.amount / 100)).toFixed(2)
                    : discountApplied.amount.toFixed(2)}
                </Text>
              </View>
            </View>
          </View>

          <View style={{ ...styles.tableHeader }}>
            <View
              style={{
                ...styles.tableCell,
                width: '15%',
                ...styles.borderLeft,
                ...styles.borderTop,
                ...styles.borderBottom,
                marginBottom: 1
              }}
            ></View>
            <View
              style={{
                ...styles.tableCell,
                width: '55%',
                backgroundColor: '#D3D3D3',
                ...styles.borderLeft,
                ...styles.borderTop,
                ...styles.borderBottom,
                marginBottom: 1
              }}
            >
              <Text style={{ fontSize: 8, ...styles.footerLabel, fontFamily: 'Roboto' }}>Sub-Total (Before Tax)</Text>
            </View>
            <View
              style={{
                ...styles.tableCell,
                width: '6%',
                ...styles.borderLeft,
                ...styles.borderTop,
                ...styles.borderBottom,
                marginBottom: 1
              }}
            ></View>
            <View
              style={{
                ...styles.tableCell,
                width: '6%',
                ...styles.borderLeft,
                ...styles.borderTop,
                ...styles.borderBottom,
                marginBottom: 1
              }}
            ></View>
            <View
              style={{
                ...styles.tableCell,
                width: '18%',
                ...styles.borderLeft,
                ...styles.borderRight,
                ...styles.borderTop,
                display: 'flex',
                flexDirection: 'row',
                paddingTop: 0,
                ...styles.borderBottom,
                marginBottom: 1
              }}
            >
              <View
                style={{
                  width: '50%',
                  height: 'auto',
                  borderRightWidth: 1,
                  borderRightStyle: 'solid',
                  borderColor: 'black',
                  marginBottom: 1
                }}
              ></View>
              <View style={{ width: '50%' }}>
                <Text style={{ fontSize: 8, paddingTop: 7, ...styles.footerValue, fontFamily: 'Roboto' }}>
                  {discountApplied.type === 'percentage'
                    ? (subTotal - (systemProductSubTotal * (discountApplied.amount / 100)).toFixed(2)).toFixed(2)
                    : (subTotal - discountApplied.amount).toFixed(2)}
                </Text>
              </View>
            </View>
          </View>
        </View>
      )}

      {/*<! -------------- Taxes Footer - TAX --------------------!> */}

      {taxesApplied.map((tax, index) => (
        <View style={{ ...styles.tableHeader }} wrap={false}>
          <View
            style={{
              ...styles.tableCell,
              width: '15%',
              ...styles.borderLeft,
              ...styles.borderTop
            }}
          ></View>
          <View
            style={{
              ...styles.tableCell,
              width: '55%',
              backgroundColor: '#D3D3D3',
              ...styles.borderLeft,
              ...styles.borderTop
            }}
          >
            <Text style={{ fontSize: 8, ...styles.footerLabel, fontFamily: 'Roboto' }}>
              {tax.name} @ {tax.amount}
              {tax.type === 'percentage' ? '%' : ''}
            </Text>
          </View>
          <View
            style={{
              ...styles.tableCell,
              width: '6%',
              ...styles.borderLeft,
              ...styles.borderTop
            }}
          ></View>
          <View
            style={{
              ...styles.tableCell,
              width: '6%',
              ...styles.borderLeft,
              ...styles.borderTop
            }}
          ></View>
          <View
            style={{
              ...styles.tableCell,
              width: '18%',
              ...styles.borderLeft,
              ...styles.borderRight,
              ...styles.borderTop,

              display: 'flex',
              flexDirection: 'row',
              paddingTop: 0
            }}
          >
            <View
              style={{
                width: '50%',
                height: 'auto',
                borderRightWidth: 1,
                borderRightStyle: 'solid',
                borderColor: 'black'
              }}
            ></View>
            <View style={{ width: '50%' }}>
              <Text style={{ fontSize: 8, paddingTop: 7, ...styles.footerValue }}>
                {tax.type === 'percentage'
                  ? subTotalAfterDiscount !== undefined
                    ? (subTotalAfterDiscount * (tax.amount / 100)).toFixed(2)
                    : (subTotal * (tax.amount / 100)).toFixed(2)
                  : tax.amount}
              </Text>
            </View>
          </View>
        </View>
      ))}

      {/*<! -------------- END Taxes Footer --------------------!> */}

      <View style={{ ...styles.tableHeader }} wrap={false}>
        <View
          style={{
            ...styles.tableCell,
            width: '15%',
            ...styles.borderTop,
            ...styles.borderLeft,
            ...styles.borderBottom
          }}
        ></View>
        <View
          style={{
            ...styles.tableCell,
            width: '55%',
            backgroundColor: '#D3D3D3',
            ...styles.borderTop,
            ...styles.borderLeft,
            ...styles.borderBottom
          }}
        >
          <Text style={{ fontSize: 8, ...styles.footerLabel, fontFamily: 'Roboto' }}>Grand Total</Text>
        </View>
        <View
          style={{
            ...styles.tableCell,
            width: '6%',
            ...styles.borderTop,
            ...styles.borderLeft,
            ...styles.borderBottom
          }}
        ></View>
        <View
          style={{
            ...styles.tableCell,
            width: '6%',
            ...styles.borderTop,
            ...styles.borderLeft,
            ...styles.borderBottom
          }}
        ></View>
        <View
          style={{
            ...styles.tableCell,
            width: '18%',
            ...styles.borderTop,
            ...styles.borderLeft,
            ...styles.borderRight,
            ...styles.borderBottom,

            display: 'flex',
            flexDirection: 'row',
            paddingTop: 0
          }}
        >
          <View
            style={{
              width: '50%',
              height: 'auto',
              borderRightWidth: 1,
              borderRightStyle: 'solid',
              borderColor: 'black'
            }}
          ></View>
          <View style={{ width: '50%' }}>
            <Text style={{ fontSize: 8, paddingTop: 7, ...styles.footerValue, fontFamily: 'Roboto' }}>{grandTotal.toFixed(2)}</Text>
          </View>
        </View>
      </View>
      <View
        style={{
          width: '100%',
          borderBottomWidth: 1, // Line thickness
          borderColor: 'black', // Line color
          marginTop: 1 // Add a little space between the row and the line
        }}
      />

      <View style={styles.paymentInfo}>
        <Text style={{ ...styles.paymentInfoText }}>Payment:</Text>
        <Text style={{ ...styles.paymentInfoText }}>
          1. All cheques should be crossed and make payable to "HOST PLATFORM SDN BHD" or Bank into our bank account MAYBANK (Account Number : 5123
          5262 3290)
        </Text>
        <Text style={{ ...styles.paymentInfoText }}>
          2. Please include our invoice number in your payment description & email to : admin@hostplatform.com
        </Text>
        <Text style={{ ...styles.paymentInfoText }}>3. No refund upon payment made</Text>
        <Text style={{ ...styles.paymentInfoText }}>4. 30% penalty charges would be imposed for any cancellation of orders</Text>
        <Text style={{ paddingBottom: 20 }}>5. All prices are subject to 8% SST charges</Text>
        <Text>Please do not hesitate to contact the undersigned for further assistance.</Text>
      </View>
    </Page>

    {/*<! -------------- PDF Inventory Summary Page --------------------!> */}

    {inventories && inventories.length > 0 ? (
      hasParentChildHost === true ? (
        <Page size="A4" style={styles.pageInventorySummary}>
          <View style={{ ...styles.tableHeader, backgroundColor: '#D3D3D3' }}>
            <View style={{ ...styles.tableCell, width: '10%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Property Name</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '10%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Unit Name</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Host</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '8%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>HostProtect</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Active</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Plan</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Identity</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '9%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Parent / Primary</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '5%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Free</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '9%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Renewal</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '10%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Created At</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '7%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Subscription Fee</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '8%', ...styles.borderTop, ...styles.borderRight, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Remark</Text>
            </View>
          </View>

          {inventories.map((inventory, index) => (
            <View key={index} style={styles.tableInventorySummaryHeader} wrap={false}>
              <View style={{ ...styles.tableCell, width: '10%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.propertyName}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '10%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.unitName}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.hostName}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '8%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>
                  {inventory.hasProtection && inventory.isProtectionActive ? inventory.hostProtectExpiredDate : '-'}
                </Text>
              </View>
              <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.active ? 'Yes' : 'No'}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.planNameLabel}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.unitConfig.unitTypeLabel}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '9%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.unitParent ? inventory.unitParent.name : '-'}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '5%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.free ? 'Yes' : 'No'}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '9%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.unitConfig.renewal}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '10%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.unitCreatedAt}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '7%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.unitConfig.subscriptionFee || inventory.unitConfig.pricingModelSubFee}</Text>
              </View>
              <View
                style={{ ...styles.tableCell, width: '8%', ...styles.borderTop, ...styles.borderRight, ...styles.borderLeft, ...styles.borderBottom }}
              >
                <Text style={{ fontSize: 8 }}>{inventory.unitConfig.remark}</Text>
              </View>
            </View>
          ))}
        </Page>
      ) : (
        <Page size="A4" style={styles.pageInventorySummary}>
          <View style={{ ...styles.tableHeader, backgroundColor: '#D3D3D3' }}>
            <View style={{ ...styles.tableCell, width: '11%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Property Name</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '11%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Unit Name</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '8%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>HostProtect</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Active</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '7%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Plan</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Identity</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '10%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Parent / Primary</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '5%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Free</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '9%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Renewal</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '10%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Created At</Text>
            </View>
            <View style={{ ...styles.tableCell, width: '7%%', ...styles.borderTop, ...styles.borderLeft }}>
              <Text style={{ fontSize: 8 }}>Subscription Fee</Text>
            </View>
            <View
              style={{
                ...styles.tableCell,
                width: '10%',
                ...styles.borderTop,
                ...styles.borderRight,
                ...styles.borderLeft
              }}
            >
              <Text style={{ fontSize: 8 }}>Remark</Text>
            </View>
          </View>

          {inventories.map((inventory, index) => (
            <View key={index} style={styles.tableInventorySummaryHeader}>
              <View style={{ ...styles.tableCell, width: '11%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.propertyName}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '11%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8, fontFamily: 'Microsoft YaHei' }}>{inventory.unitName}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '8%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>
                  {inventory.hasProtection && inventory.isProtectionActive ? inventory.hostProtectExpiredDate : '-'}
                </Text>
              </View>
              <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.active ? 'Yes' : 'No'}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '7%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.planNameLabel}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '6%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.unitConfig.unitTypeLabel}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '10%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.unitParent ? inventory.unitParent.name : '-'}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '5%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.free ? 'Yes' : 'No'}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '9%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.unitConfig.renewal}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '10%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.unitCreatedAt}</Text>
              </View>
              <View style={{ ...styles.tableCell, width: '7%', ...styles.borderTop, ...styles.borderLeft, ...styles.borderBottom }}>
                <Text style={{ fontSize: 8 }}>{inventory.unitConfig.subscriptionFee || inventory.unitConfig.pricingModelSubFee}</Text>
              </View>
              <View
                style={{
                  ...styles.tableCell,
                  width: '10%',
                  ...styles.borderTop,
                  ...styles.borderRight,
                  ...styles.borderLeft,
                  ...styles.borderBottom
                }}
              >
                <Text style={{ fontSize: 8 }}>{inventory.unitConfig.remark}</Text>
              </View>
            </View>
          ))}
        </Page>
      )
    ) : null}
  </Document>
);

export default SubscriptionInvoicePDF;
